/* ---------------------------------------------------------------------------
(c) 2023-2024, HID Global Corporation, part of ASSA ABLOY.
All rights reserved.

This program is an un published copyrighted work which is proprietary
to HID Global Corporation. This computer program includes Confidential,
Proprietary Information and is a Trade Secret of HID Global Corporation.
Any use, disclosure, modification and/or reproduction is prohibited
unless authorized in writing by HID Global Corporation.

WARNING:  Unauthorized reproduction of this program as well as
unauthorized preparation of derivative works based upon the
program or distribution of copies by sale, rental, lease or
lending are violations of federal copyright laws and state trade
secret laws, punishable by civil and criminal penalties.
--------------------------------------------------------------------------- */
import { Injectable } from '@angular/core';
import { HttpRequest, HttpResponse, HttpErrorResponse, HttpHandler, HttpEvent, HttpInterceptor, HttpXsrfTokenExtractor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

@Injectable()
export class HttpXsrfInterceptor implements HttpInterceptor {
  headerName = 'server-csrf-token';

  constructor(private tokenExtractor: HttpXsrfTokenExtractor) {
    // Clean previous token.
    sessionStorage.removeItem(this.headerName);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Intercept only /authn/code, /authn/consent and /authn/enroll/XXX requests / responses.
    if (
      !req.url.endsWith('/authn/code') &&
      !req.url.endsWith('/authn/consent') &&
      !req.url.includes('/authn/enroll/')
    ) {
      return next.handle(req);
    }

    // Look for XSRF token in session storage (this token is set after authentication).
    let xsrfToken = sessionStorage.getItem(this.headerName);
    if (!xsrfToken) {
      // If not found, look in cookies (token set before authent and used if auth portal and backend API are on the same domain).
      xsrfToken = this.tokenExtractor.getToken() as string;
    }

    // If found, set the XSRF token in requests headers.
    if (xsrfToken !== null && !req.headers.has(this.headerName)) {
      req = req.clone({ headers: req.headers.set(this.headerName, xsrfToken) });
    }

    return next.handle(req).pipe(
      // Look for the XSRF token in responses headers.
      // If found, store it in the session storage in order to pass it for subsequent requests.
      // Note that we check headers from reponses AND error responses as, for instance, the first authent may respond
      // an error with step-up required message and deliver a XSRF token with this response.
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.getXSRFTokenFromHeaders(event);
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          this.getXSRFTokenFromHeaders(error);
        }
        return throwError(error);
      })
    );
  }

  private getXSRFTokenFromHeaders(event: HttpResponse<any> | HttpErrorResponse) {
    if (event.headers.has(this.headerName)) {
      const serverXsrfToken = event.headers.get(this.headerName);
      if (serverXsrfToken) {
        sessionStorage.setItem(this.headerName, serverXsrfToken);
      }
    }
  }
}
